import { computed } from 'vue'
import { freezeObject, isFunction } from '@/global/utils/utils'
import { FORMATS } from '@/global/services/date'

export function useCreate (options) {
  const {
    trans,
    columnsContentComponent,
    tabActionsComponent,
    onCancelCreateFn,
    onSavedFn,
    unitOfMeasureItems
  } = options || {}

  const apiConfig = freezeObject({
    post: {
      module: 'road-maintenance',
      route: 'products'
    }
  })

  const containerStyle = freezeObject({
    padding: 0
  })

  const basicInfoCard = computed(() => ({
    component: columnsContentComponent,
    showButtons: false,
    fullWidth: true,
    rows: [
      {
        type: 'fields',
        columnsCount: 2,
        fields: [
          {
            key: 'description',
            label: trans('road-maintenance/patrol-service.description'),
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'barcode',
            label: trans('road-maintenance/inventory.barcode'),
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'type_id',
            label: trans('road-maintenance/inventory.item_category'),
            type: 'autocomplete',
            autocomplete_options: {
              module: 'road-maintenance',
              route: 'product-types',
              custom_display_value: (item) => {
                return trans(item?.description || '')
              },
              allowSearch: false
            },
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'unit_of_measure_id',
            label: trans('road-maintenance/settings.unit_of_measure'),
            type: 'autocomplete',
            items: unitOfMeasureItems.value,
            autocomplete_options: {
              custom_display_value: (item) => {
                return trans(item?.description || '')
              }
            },
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'images',
            type: 'images-stack',
            label: trans('road-maintenance/patrol-service.photos'),
            maxImages: 3,
            deletedArrayKey: 'images-deleted',
            creatable: true,
            editable: true,
            visible: true
          }
        ]
      }
    ]
  }))

  const additionalInfoCard = computed(() => ({
    subTitle: trans('road-maintenance/inventory.product_details'),
    component: columnsContentComponent,
    fullWidth: true,
    rows: [
      {
        type: 'fields',
        columnsCount: 4,
        fields: [
          {
            key: 'manufacturer',
            label: trans('road-maintenance/inventory.manufacturer'),
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'manufacturer_code',
            label: trans('road-maintenance/inventory.manufacturer_code'),
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'manufacture_date',
            label: trans('road-maintenance/inventory.manufacturer_date'),
            type: 'datetime-picker',
            subType: 'date',
            appendIcon: 'mdi-calendar-month',
            format: FORMATS.isoDate,
            placeholder: FORMATS.isoDate,
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'warranty_expiry_date',
            label: trans('road-maintenance/inventory.warranty_valid_until'),
            type: 'datetime-picker',
            subType: 'date',
            appendIcon: 'mdi-calendar-month',
            format: FORMATS.isoDate,
            placeholder: FORMATS.isoDate,
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'length',
            label: trans('base.length') + ' [cm]',
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'width',
            label: trans('base.width') + ' [cm]',
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'height',
            label: trans('base.height') + ' [cm]',
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'weight',
            label: trans('base.weight') + ' [kg]',
            creatable: true,
            editable: true,
            visible: true
          }
        ]
      }
    ]
  }))

  const onProductSaved = () => {
    if (isFunction(onSavedFn)) {
      onSavedFn()
    }
  }

  const onCancelTabSave = (event) => {
    const { cancelTabSave } = event || {}

    if (isFunction(cancelTabSave)) {
      cancelTabSave()
    }

    if (isFunction(onCancelCreateFn)) {
      onCancelCreateFn()
    }
  }

  const tabActions = computed(() => ({
    component: tabActionsComponent,
    title: trans('fleet/vehicle.basic_info'),
    showEditButtons: true,
    onCancelTabSave
  }))

  return computed(() => ({
    containerStyle,
    tabs: [
      {
        title: trans('fleet/vehicle.basic_info'),
        apiConfig,
        afterSave: onProductSaved,
        tabContent: [
          tabActions.value,
          basicInfoCard.value,
          additionalInfoCard.value
        ]
      }
    ]
  }))
}
